import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FleetInstallation, RegisterInstallationRequest } from '@hospasense/contracts';
import { fetchFleetInstallations, registerInstallation } from '../lib/queries-fleet.js';
import { fetchOrganisationsList } from '../lib/queries-commercial.js';
import { RegisterInstallationModal } from './fleet-modals.js';
import { useAuth } from '../lib/auth.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../components/ui/index.js';

function getDeploymentProfileBadge(profile: string) {
  if (profile === 'cloud_managed') {
    return {
      label: 'Core Flagship Cloud',
      subtext: 'SenseSys Operated',
      tone: 'accent' as const,
    };
  }
  if (profile === 'cloud_managed_single_tenant' || profile === 'cloud_multi_tenant') {
    return {
      label: 'White-Label Dedicated Cloud',
      subtext: 'Partner / Dedicated VPC',
      tone: 'info' as const,
    };
  }
  if (profile === 'standalone_onprem' || profile === 'ha_cluster_onprem') {
    return {
      label: 'Customer On-Premises',
      subtext: 'Hospital Server / LAN',
      tone: 'warning' as const,
    };
  }
  return {
    label: profile.replace(/_/g, ' '),
    subtext: 'Custom Topology',
    tone: 'neutral' as const,
  };
}

export function FleetPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('fleet.manage');

  const [installations, setInstallations] = useState<FleetInstallation[]>([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [topologyFilter, setTopologyFilter] = useState<'all' | 'core' | 'whitelabel' | 'onprem'>(
    'all',
  );
  const [ringFilter, setRingFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fleetData, orgsData] = await Promise.all([
        fetchFleetInstallations({
          ring: ringFilter !== 'all' ? ringFilter : undefined,
          state: stateFilter !== 'all' ? stateFilter : undefined,
          search: search || undefined,
        }),
        fetchOrganisationsList(),
      ]);
      setInstallations(fleetData.items);
      setOrganisations(orgsData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [ringFilter, stateFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void loadData();
  };

  const handleRegister = async (data: RegisterInstallationRequest) => {
    await registerInstallation(data);
    await loadData();
  };

  const connectedCount = installations.filter((i) => i.connectivityState === 'connected').length;
  const degradedCount = installations.filter((i) => i.connectivityState === 'degraded').length;
  const staleCount = installations.filter(
    (i) => i.connectivityState === 'stale' || i.connectivityState === 'offline',
  ).length;

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <PageHeader
        title="Fleet Operations"
        subtitle="Monitor, inspect, and configure distributed cloud and on-premises hospital installations."
        actions={
          canManage && (
            <Button
              variant="primary"
              data-testid="register-installation-button"
              onClick={() => setIsRegisterOpen(true)}
            >
              Register Installation
            </Button>
          )
        }
      />

      {/* Metrics Row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Total Nodes
            </span>
            <p className="mt-1 text-2xl font-bold text-text-primary tabular-nums">
              {installations.length}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-success">
              Connected
            </span>
            <p className="mt-1 text-2xl font-bold text-success tabular-nums">{connectedCount}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-warning">
              Degraded
            </span>
            <p className="mt-1 text-2xl font-bold text-warning tabular-nums">{degradedCount}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-error">
              Stale / Offline
            </span>
            <p className="mt-1 text-2xl font-bold text-error tabular-nums">{staleCount}</p>
          </CardBody>
        </Card>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardBody className="space-y-3">
          {/* Topology Category Tabs */}
          <div className="flex flex-wrap items-center gap-1 border-b border-border pb-3">
            {[
              { key: 'all', label: 'All Deployments' },
              { key: 'core', label: '★ Core Flagship Cloud' },
              { key: 'whitelabel', label: '🏢 White-Label & Dedicated' },
              { key: 'onprem', label: '🏥 Customer On-Premises' },
            ].map((tab) => (
              <Button
                key={tab.key}
                size="sm"
                variant={topologyFilter === tab.key ? 'primary' : 'ghost'}
                onClick={() => setTopologyFilter(tab.key as typeof topologyFilter)}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Ring:
              </span>
              {['all', 'internal', 'pilot', 'general'].map((ring) => (
                <Button
                  key={ring}
                  size="sm"
                  variant={ringFilter === ring ? 'primary' : 'outline'}
                  onClick={() => setRingFilter(ring)}
                >
                  {ring.charAt(0).toUpperCase() + ring.slice(1)}
                </Button>
              ))}
              <span className="ml-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                State:
              </span>
              {['all', 'connected', 'degraded', 'stale', 'offline'].map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={stateFilter === st ? 'secondary' : 'ghost'}
                  onClick={() => setStateFilter(st)}
                >
                  {st.charAt(0).toUpperCase() + st.slice(1)}
                </Button>
              ))}
            </div>

            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <input
                type="text"
                placeholder="Search identifier or org..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-border bg-bg-s1 px-3 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:border-border focus:ring-1 focus:ring-accent focus:outline-none sm:w-64"
              />
              <Button size="sm" variant="secondary" type="submit">
                Filter
              </Button>
            </form>
          </div>
        </CardBody>
      </Card>

      {error && (
        <ErrorState
          description={error}
          onRetry={() => {
            void loadData();
          }}
        />
      )}

      {/* Fleet Table */}
      {loading ? (
        <LoadingState label="Loading fleet topology..." />
      ) : installations.length === 0 ? (
        <EmptyState
          title="No installations match the filters"
          description="Adjust your search filters or register a new installation node."
        />
      ) : (
        <TableContainer>
          <Table density="compact">
            <THead>
              <TR>
                <TH>Installation</TH>
                <TH>Organisation</TH>
                <TH>Deployment Topology</TH>
                <TH>Ring</TH>
                <TH>Version</TH>
                <TH>Status</TH>
                <TH align="right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {installations
                .filter((inst) => {
                  if (topologyFilter === 'core') return inst.deploymentProfile === 'cloud_managed';
                  if (topologyFilter === 'whitelabel')
                    return (
                      inst.deploymentProfile === 'cloud_managed_single_tenant' ||
                      inst.deploymentProfile === 'cloud_multi_tenant'
                    );
                  if (topologyFilter === 'onprem')
                    return (
                      inst.deploymentProfile === 'standalone_onprem' ||
                      inst.deploymentProfile === 'ha_cluster_onprem'
                    );
                  return true;
                })
                .map((inst) => {
                  const statusTone =
                    inst.connectivityState === 'connected'
                      ? 'success'
                      : inst.connectivityState === 'degraded'
                        ? 'warning'
                        : 'error';

                  const ringTone =
                    inst.rolloutRing === 'internal'
                      ? 'accent'
                      : inst.rolloutRing === 'pilot'
                        ? 'warning'
                        : 'success';

                  const badge = getDeploymentProfileBadge(inst.deploymentProfile);

                  return (
                    <TR key={inst.id}>
                      <TD>
                        <div className="font-semibold text-text-primary">{inst.displayName}</div>
                        <div className="font-mono text-xs text-text-muted">
                          {inst.installationIdentifier}
                        </div>
                      </TD>
                      <TD>{inst.organisationName ?? '—'}</TD>
                      <TD>
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                        <div className="text-[10px] text-text-muted mt-0.5">
                          {badge.subtext} · <span className="uppercase">{inst.environment}</span>
                        </div>
                      </TD>
                      <TD>
                        <Badge tone={ringTone}>{inst.rolloutRing}</Badge>
                      </TD>
                      <TD className="font-mono">{inst.installedVersion}</TD>
                      <TD>
                        <StatusPill tone={statusTone} label={inst.connectivityState} />
                        {inst.heartbeatAgeSeconds !== null &&
                          inst.heartbeatAgeSeconds !== undefined && (
                            <div className="text-xs text-text-muted">
                              {inst.heartbeatAgeSeconds < 60
                                ? 'Just now'
                                : `${Math.round(inst.heartbeatAgeSeconds / 60)}m ago`}
                            </div>
                          )}
                      </TD>
                      <TD align="right">
                        <Link to={`/fleet/${inst.installationIdentifier}`}>
                          <Button size="sm" variant="outline">
                            Inspect →
                          </Button>
                        </Link>
                      </TD>
                    </TR>
                  );
                })}
            </TBody>
          </Table>
        </TableContainer>
      )}

      <RegisterInstallationModal
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        organisations={organisations}
        onSubmit={handleRegister}
      />
    </div>
  );
}
